/**
 * Mermaid Tool - Canvas 平移/縮放控制
 * static/js/mermaid/canvas.js
 */

import { getState, updateState } from './state.js';
import { ZOOM_LIMITS } from './config.js';
import { showToast } from './ui.js';

// DOM 元素參照
let canvasContainer = null;
let canvasContent = null;

/**
 * 初始化 Canvas 控制
 * @param {HTMLElement} container - 容器元素
 * @param {HTMLElement} content - 內容元素
 */
export function initCanvas(container, content) {
    canvasContainer = container;
    canvasContent = content;
    
    if (!canvasContainer || !canvasContent) {
        console.warn('Canvas elements not found');
        return;
    }
    
    setupMouseEvents();
    setupTouchEvents();
    setupZoomControls();
}

/**
 * 設定滑鼠事件
 */
function setupMouseEvents() {
    // 滑鼠滾輪縮放
    canvasContainer.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const state = getState();
        const newZoom = Math.max(ZOOM_LIMITS.MIN, Math.min(ZOOM_LIMITS.MAX, state.canvasZoom + delta));
        setZoom(newZoom);
    }, { passive: false });
    
    // 滑鼠拖曳平移
    canvasContainer.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return; // 只處理左鍵
        
        const state = getState();
        updateState({
            isPanning: true,
            panStartX: e.clientX - state.canvasPanX,
            panStartY: e.clientY - state.canvasPanY
        });
        canvasContent.classList.add('panning');
    });
    
    document.addEventListener('mousemove', (e) => {
        const state = getState();
        if (!state.isPanning) return;
        
        updateState({
            canvasPanX: e.clientX - state.panStartX,
            canvasPanY: e.clientY - state.panStartY
        });
        updateTransform();
    });
    
    document.addEventListener('mouseup', () => {
        updateState({ isPanning: false });
        if (canvasContent) {
            canvasContent.classList.remove('panning');
        }
    });
}

/**
 * 設定觸控事件
 */
function setupTouchEvents() {
    let initialDistance = null;
    
    canvasContainer.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
            // 單指平移
            const state = getState();
            updateState({
                isPanning: true,
                panStartX: e.touches[0].clientX - state.canvasPanX,
                panStartY: e.touches[0].clientY - state.canvasPanY
            });
            canvasContent.classList.add('panning');
        } else if (e.touches.length === 2) {
            // 雙指縮放
            initialDistance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
        }
    }, { passive: true });
    
    canvasContainer.addEventListener('touchmove', (e) => {
        const state = getState();
        
        if (e.touches.length === 1 && state.isPanning) {
            updateState({
                canvasPanX: e.touches[0].clientX - state.panStartX,
                canvasPanY: e.touches[0].clientY - state.panStartY
            });
            updateTransform();
        } else if (e.touches.length === 2 && initialDistance) {
            const currentDistance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const scale = currentDistance / initialDistance;
            const newZoom = Math.max(ZOOM_LIMITS.MIN, Math.min(ZOOM_LIMITS.MAX, state.canvasZoom * scale));
            setZoom(newZoom);
            initialDistance = currentDistance;
        }
    }, { passive: true });
    
    canvasContainer.addEventListener('touchend', () => {
        updateState({ isPanning: false });
        initialDistance = null;
        canvasContent.classList.remove('panning');
    });
}

/**
 * 設定縮放控制按鈕
 */
function setupZoomControls() {
    document.getElementById('zoomInBtn')?.addEventListener('click', () => {
        const state = getState();
        setZoom(Math.min(ZOOM_LIMITS.MAX, state.canvasZoom + ZOOM_LIMITS.STEP));
    });
    
    document.getElementById('zoomOutBtn')?.addEventListener('click', () => {
        const state = getState();
        setZoom(Math.max(ZOOM_LIMITS.MIN, state.canvasZoom - ZOOM_LIMITS.STEP));
    });
    
    document.getElementById('zoomResetBtn')?.addEventListener('click', () => {
        resetView();
    });
    
    document.getElementById('zoomFitBtn')?.addEventListener('click', () => {
        zoomToFit();
    });
    
    document.getElementById('zoomFullscreenBtn')?.addEventListener('click', () => {
        toggleFullscreen();
    });
}

/**
 * 限制縮放值在允許範圍內
 * @param {number} zoom - 縮放值
 * @returns {number} 限制後的縮放值
 */
export function clampZoom(zoom) {
    return Math.max(ZOOM_LIMITS.MIN, Math.min(ZOOM_LIMITS.MAX, zoom));
}

/**
 * 計算適應容器的縮放比例
 * @param {number} contentWidth - 內容寬度
 * @param {number} contentHeight - 內容高度
 * @param {number} containerWidth - 容器寬度
 * @param {number} containerHeight - 容器高度
 * @param {number} [padding=0.9] - 邊距係數 (0-1)
 * @returns {number} 計算的縮放比例
 */
export function calculateFitZoom(contentWidth, contentHeight, containerWidth, containerHeight, padding = 0.9) {
    if (!contentWidth || !contentHeight || !containerWidth || !containerHeight) {
        return 1;
    }
    
    const scaleX = (containerWidth * padding) / contentWidth;
    const scaleY = (containerHeight * padding) / contentHeight;
    const scale = Math.min(scaleX, scaleY);
    
    // 限制在合理範圍內
    return Math.max(ZOOM_LIMITS.MIN, Math.min(ZOOM_LIMITS.FIT_MAX, scale));
}

/**
 * 設定縮放比例
 * @param {number} zoom - 縮放比例
 */
export function setZoom(zoom) {
    updateState({ canvasZoom: zoom });
    updateTransform();
    updateZoomDisplay();
}

/**
 * 更新 Canvas 變換
 */
export function updateTransform() {
    if (!canvasContent) return;
    
    const state = getState();
    canvasContent.style.transform = `translate(${state.canvasPanX}px, ${state.canvasPanY}px) scale(${state.canvasZoom})`;
}

/**
 * 更新縮放顯示
 */
export function updateZoomDisplay() {
    const zoomLevel = document.getElementById('zoomLevel');
    if (zoomLevel) {
        const state = getState();
        zoomLevel.textContent = Math.round(state.canvasZoom * 100) + '%';
    }
}

/**
 * 重置視圖
 */
export function resetView() {
    updateState({
        canvasZoom: 1,
        canvasPanX: 0,
        canvasPanY: 0
    });
    updateTransform();
    updateZoomDisplay();
}

/**
 * 縮放至適合容器大小
 */
export function zoomToFit() {
    if (!canvasContainer || !canvasContent) return;
    
    const svg = canvasContent.querySelector('svg');
    if (!svg) {
        resetView();
        return;
    }
    
    const state = getState();
    const containerRect = canvasContainer.getBoundingClientRect();
    const svgRect = svg.getBoundingClientRect();
    
    // 取得 SVG 原始尺寸
    const svgWidth = svg.viewBox?.baseVal?.width || svg.width?.baseVal?.value || svgRect.width / state.canvasZoom;
    const svgHeight = svg.viewBox?.baseVal?.height || svg.height?.baseVal?.value || svgRect.height / state.canvasZoom;
    
    if (svgWidth === 0 || svgHeight === 0) {
        resetView();
        return;
    }
    
    // 計算縮放比例
    const padding = 40;
    const scaleX = (containerRect.width - padding) / svgWidth;
    const scaleY = (containerRect.height - padding) / svgHeight;
    const newZoom = Math.min(scaleX, scaleY, ZOOM_LIMITS.FIT_MAX);
    
    updateState({
        canvasZoom: Math.max(ZOOM_LIMITS.MIN, newZoom),
        canvasPanX: 0,
        canvasPanY: 0
    });
    updateTransform();
    updateZoomDisplay();
}

/**
 * 切換全螢幕模式
 */
export function toggleFullscreen() {
    const previewArea = document.getElementById('previewArea');
    if (!previewArea) return;
    
    if (document.fullscreenElement) {
        document.exitFullscreen();
    } else {
        previewArea.requestFullscreen().catch(err => {
            showToast('無法進入全螢幕模式', 'warning');
        });
    }
}

// ============== Panel Resizer ==============

/**
 * 初始化面板大小調整器
 */
export function initPanelResizer() {
    const resizer = document.getElementById('panelResizer');
    const codePanel = document.querySelector('.code-panel');
    const editorContainer = document.querySelector('.editor-container');
    
    if (!resizer || !codePanel || !editorContainer) return;
    
    resizer.addEventListener('mousedown', (e) => {
        const state = getState();
        updateState({
            isResizing: true,
            resizeStartX: e.clientX,
            resizeStartWidth: codePanel.offsetWidth
        });
        resizer.classList.add('dragging');
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        e.preventDefault();
    });
    
    document.addEventListener('mousemove', (e) => {
        const state = getState();
        if (!state.isResizing) return;
        
        const containerWidth = editorContainer.offsetWidth;
        const dx = e.clientX - state.resizeStartX;
        let newWidth = state.resizeStartWidth + dx;
        
        // 限制範圍
        const minWidth = 150;
        const maxWidth = containerWidth * 0.6;
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        
        const percentage = (newWidth / containerWidth) * 100;
        codePanel.style.flex = `0 0 ${percentage}%`;
    });
    
    document.addEventListener('mouseup', () => {
        const state = getState();
        if (state.isResizing) {
            updateState({ isResizing: false });
            const resizer = document.getElementById('panelResizer');
            resizer?.classList.remove('dragging');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            
            // 重新適應圖表大小
            setTimeout(zoomToFit, 100);
        }
    });
    
    // 觸控支援
    resizer.addEventListener('touchstart', (e) => {
        updateState({
            isResizing: true,
            resizeStartX: e.touches[0].clientX,
            resizeStartWidth: codePanel.offsetWidth
        });
        resizer.classList.add('dragging');
    }, { passive: true });
    
    document.addEventListener('touchmove', (e) => {
        const state = getState();
        if (!state.isResizing) return;
        
        const containerWidth = editorContainer.offsetWidth;
        const dx = e.touches[0].clientX - state.resizeStartX;
        let newWidth = state.resizeStartWidth + dx;
        
        const minWidth = 150;
        const maxWidth = containerWidth * 0.6;
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        
        const percentage = (newWidth / containerWidth) * 100;
        codePanel.style.flex = `0 0 ${percentage}%`;
    }, { passive: true });
    
    document.addEventListener('touchend', () => {
        const state = getState();
        if (state.isResizing) {
            updateState({ isResizing: false });
            const resizer = document.getElementById('panelResizer');
            resizer?.classList.remove('dragging');
            setTimeout(zoomToFit, 100);
        }
    });
}
